import React from 'react';
import { FormField } from '@workday/canvas-kit-preview-react/form-field';
import styles from './phone-number-text-field.module.scss';
import { autoId } from '@workday/upc-common';
import { Select, TextInput } from '@workday/canvas-kit-react';
import { countryCodes, CountryCode, findCountryCode } from '../../utils/countryCodes';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

export const ID_PHONE_NUMBER_FIELD = 'phone-number-field';

interface PhoneNumberTextFieldProps {
    autoFocus?: boolean;
    className?: string;
    countryCode: CountryCode;
    onCountryCodeChange: (countryCode: CountryCode) => void;
    errorMessage?: string;
    grow: boolean;
    id: string;
    isDisabled: boolean;
    isInvalid: boolean;
    label: React.ReactElement | string;
    countryCodeLabel: string;
    phoneNumberLabel: string;
    onChange: (e: React.FormEvent<HTMLInputElement>) => void;
    value?: string;
    defaultValue: string;
    placeholder: string;
}

export const PhoneNumberTextField = React.forwardRef<HTMLElement, PhoneNumberTextFieldProps>(
    (
        {
            autoFocus,
            className,
            countryCode = findCountryCode(),
            onCountryCodeChange,
            errorMessage,
            grow,
            id,
            isDisabled,
            isInvalid,
            label,
            countryCodeLabel,
            phoneNumberLabel,
            onChange,
            value,
            defaultValue,
            placeholder
        },
        ref
    ) => {
        const { t } = useTranslation('account');
        const options = countryCodes.map(country => {
            return {
                id: country.shortName,
                text: `${country.shortName} ${country.code}`,
                longText: t(country.countryTranslationKey).replace('{phoneCountryCode}', country.code)
            };
        });

        return (
            <div className={classNames(styles.phoneNumberTextField, className)}>
                <div {...autoId('phoneContainer')} className={styles.inputContainer}>
                    <FormField
                        as={'fieldset'}
                        {...autoId(ID_PHONE_NUMBER_FIELD)}
                        error={isInvalid ? 'error' : undefined}
                        grow={grow}>
                        <FormField.Label as={'legend'} flexDirection={'column'} alignItems={'flex-start'}>
                            {label}
                        </FormField.Label>
                        <FormField.Container className={styles.formFieldContainer}>
                            <div className={styles.countryNameLabel}>{`${countryCode?.shortName}`}</div>
                            <Select items={options}>
                                <FormField.Input
                                    as={Select.Input}
                                    aria-label={countryCodeLabel}
                                    onChange={e => {
                                        const newValue = findCountryCode({ shortName: e.target.value });
                                        onCountryCodeChange(newValue);
                                    }}
                                    className={styles.countryCodeSelection}
                                    disabled={isDisabled}
                                    defaultValue={countryCode.shortName}
                                    placeholder={''}
                                />
                                <Select.Popper>
                                    <Select.Card width='280px'>
                                        <Select.List>{item => <Select.Item>{item.longText}</Select.Item>}</Select.List>
                                    </Select.Card>
                                </Select.Popper>
                            </Select>
                            <div className={styles.countryCodeLabel}>{`${countryCode?.code}`}</div>
                            <FormField.Input
                                as={TextInput}
                                aria-label={phoneNumberLabel}
                                autoFocus={autoFocus}
                                autoComplete='off'
                                {...autoId(id)}
                                className={styles.textInput}
                                disabled={isDisabled}
                                id={id}
                                maxLength={10}
                                name={id}
                                onChange={onChange}
                                placeholder={placeholder}
                                // @ts-ignore
                                ref={ref}
                                type='tel'
                                defaultValue={defaultValue}
                            />
                        </FormField.Container>
                        {isInvalid && errorMessage && (
                            <FormField.Hint {...autoId('error-' + id)}>{errorMessage}</FormField.Hint>
                        )}
                    </FormField>
                </div>
            </div>
        );
    }
);
